import { InvestorDetailsGetInvestorLegalEntitiesQuery, InvestorDetailsHouseholdNotesQuery } from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import { BurgerMenuIcon } from '@/assets/static/icons/burger-menu';
import { CloseMenuIcon } from '@/assets/static/icons/close-menu';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import Tabs, {
  ITab,
  MainTabsMobile,
  TabMobileWrap,
  TabsControl,
  TabsDetailsTitleMobile,
  TabsLine,
  TabsWrapMobile,
  TabTitle
} from '@/components/basicComponents/tabs';
import Tag from '@/components/basicComponents/tag';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitleWrap } from '@/components/header/pageTitle';
import MobileModal from '@/components/mobileModal';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import DeleteNote from './modals/deleteNote';
import EditNote from './modals/editNote';
import MobileNotes from './notesPane/mobileNotes';
import NotesPane from './notesPane/notes';
import NotesButton from './notesPane/notesButton';
import { INVESTOR_DETAILS_GROUP_NOTES_QUERY, INVESTOR_DETAILS_HOUSEHOLD_NOTES_QUERY, INVESTOR_DETAILS_LEGAL_ENTITY_NOTES_QUERY } from './queries';
import { InvestorDetailsEntityTypeEnum, InvestorDetailsTabsEnum } from './types';

const parseNotes = (notes: string) => {
  try {
    return JSON.parse(notes);
  } catch (error) {
    return null;
  }
};

interface InvestorDetailsLayoutProps {
  children: React.ReactNode;
  entityType: InvestorDetailsEntityTypeEnum;
  investorData: {
    investorName: string;
    advisorName: string;
    legalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
    legalEntitiesTotalCount: number;
  };
  selectedGroupName: string;
  entitiesSelectOptions: ISelectValue[];
  tabs: ITab[];
  resetEntityType: () => void;
}

export const InvestorDetailsLayout = ({
  children,
  entityType,
  investorData,
  selectedGroupName,
  entitiesSelectOptions,
  tabs,
  resetEntityType
}: InvestorDetailsLayoutProps) => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const params = useParams();
  const { isMobile, isDesktop, isTablet, isLargeDesktop } = useResponsive();
  const { searchParams, onChangeActiveTab } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab: InvestorDetailsTabsEnum = (searchParams.get('activeTab') as InvestorDetailsTabsEnum) || InvestorDetailsTabsEnum.SUMMARY;
  const isGroup = searchParams.get('isGroup') === 'true';

  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);
  const [isNotesPaneOpen, setIsNotesPaneOpen] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [notesCount, setNotesCount] = useState(0);
  const [currentNote, setCurrentNote] = useState<{ legalEntityId: string; date: string; note: string } | null>(null);
  const [aboutInvestorNotes, setAboutInvestorNotes] = useState<{ id: string; entityName: string; note: string }[]>([]);
  const [legalEntityNotes, setLegalEntityNotes] = useState<InvestorDetailsHouseholdNotesQuery['getInvestor']['legalEntityNotes']>([]);
  const [onHold, setOnHold] = useState(false);

  useQuery(INVESTOR_DETAILS_HOUSEHOLD_NOTES_QUERY, {
    skip: !investorId || !entityType || entityType !== InvestorDetailsEntityTypeEnum.Household,
    variables: {
      data: {
        id: investorId
      }
    },
    onCompleted: (data) => {
      const { legalEntityNotes, legalEntities } = data.getInvestor;

      const aboutInvestorNotes = legalEntities.flatMap((entity) => {
        if (entity.entities?.length > 0) {
          return entity.entities.flatMap((groupedEntity) => {
            if (groupedEntity.aboutInvestorNote) {
              return [{ id: groupedEntity.id, entityName: groupedEntity.entityName, note: groupedEntity.aboutInvestorNote }];
            } else {
              return [];
            }
          });
        } else {
          if (entity.aboutInvestorNote) {
            return [{ id: entity.id, entityName: entity.entityName, note: entity.aboutInvestorNote }];
          } else {
            return [];
          }
        }
      });

      setAboutInvestorNotes(aboutInvestorNotes);
      const aboutInvestorNotesCount = aboutInvestorNotes?.length || 0;
      const legalEntityNotesCount = legalEntityNotes?.reduce((acc, note) => acc + note.notes.length, 0) || 0;
      setNotesCount(aboutInvestorNotesCount + legalEntityNotesCount);
      setLegalEntityNotes(legalEntityNotes || []);
    }
  });

  useQuery(INVESTOR_DETAILS_LEGAL_ENTITY_NOTES_QUERY, {
    skip: !entityId || !entityType || entityType === InvestorDetailsEntityTypeEnum.Group,
    variables: {
      id: entityId
    },
    onCompleted: (data) => {
      const { entityName, notes, aboutInvestorNote, onHold } = data.legalEntity;

      setAboutInvestorNotes(aboutInvestorNote ? [{ id: entityId, entityName, note: aboutInvestorNote }] : []);
      const aboutInvestorNotesCount = aboutInvestorNote ? 1 : 0;
      const legalEntityNotesCount = notes?.length || 0;
      setNotesCount(aboutInvestorNotesCount + legalEntityNotesCount);
      setLegalEntityNotes([{ legalEntityId: entityId, name: entityName, notes: notes || [] }]);
      setOnHold(onHold);
    }
  });

  useQuery(INVESTOR_DETAILS_GROUP_NOTES_QUERY, {
    skip: !entityId || !entityType || entityType !== InvestorDetailsEntityTypeEnum.Group,
    variables: {
      data: {
        primaryEntityId: entityId
      }
    },
    onCompleted: (data) => {
      const { legalEntityNotes, legalEntities, onHold } = data.group;

      const aboutInvestorNotes = legalEntities.flatMap((entity) => {
        if (entity.aboutInvestorNote) {
          return [{ id: entity.id, entityName: entity.entityName, note: entity.aboutInvestorNote }];
        } else {
          return [];
        }
      });

      setAboutInvestorNotes(aboutInvestorNotes);
      const aboutInvestorNotesCount = aboutInvestorNotes?.length || 0;
      const legalEntityNotesCount = legalEntityNotes?.reduce((acc, note) => acc + note.notes.length, 0) || 0;
      setNotesCount(aboutInvestorNotesCount + legalEntityNotesCount);
      setLegalEntityNotes(legalEntityNotes || []);
      setOnHold(onHold);
    }
  });

  const handleEntitySelect = (entityId: string) => {
    resetEntityType();
    let entityIdToNavigate = entityId;
    if (entityId?.includes('group-')) {
      entityIdToNavigate = entityId?.split('group-')?.[1];
    }
    const queryParams = new URLSearchParams({
      backToTitle,
      activeTab,
      ...(entityIdToNavigate && { isGroup: entityId?.includes('group-') ? 'true' : 'false' })
    });
    navigate(`/investors/${investorId}${entityIdToNavigate && `/${entityIdToNavigate}`}?${queryParams.toString()}`, { replace: true });
    if (isMobile) {
      setIsTabMenuOpen(false);
    }
  };

  const handleModalWindow = ({ type, note }: { type: string; note?: { legalEntityId: string; date: string; note: string } | null }) => {
    setModalWindow({ type, isOpen: !!type });
    setCurrentNote(note || null);
    if (isMobile) {
      handleNotesPane({ open: !type });
    }
  };

  const handleNotesPane = ({ open }: { open: boolean }) => {
    setIsNotesPaneOpen(open);
  };

  const handleMobileTabClick = (tab: string) => {
    onChangeActiveTab(tab, activeTab, { backToTitle, isGroup });
    setIsTabMenuOpen(false);
  };

  const openEditInvestorPage = () => {
    if (entityId) {
      navigate(`/investors/${investorId}/${entityId}/edit?activeTab=${activeTab}&backToTitle=Investor Details&isGroup=${isGroup}`);
      return;
    }
    navigate(`/investors/${investorId}/edit?activeTab=${activeTab}&backToTitle=Investor Details`);
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-note' && (
        <DeleteNote
          isOpen={modalWindow.isOpen}
          entityType={entityType}
          currentNote={currentNote}
          legalEntityNotes={legalEntityNotes}
          onClose={() => handleModalWindow({ type: '', note: null })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'edit-note' && (
        <EditNote
          isOpen={modalWindow.isOpen}
          entityType={entityType}
          currentNote={currentNote}
          legalEntityNotes={legalEntityNotes}
          legalEntities={investorData.legalEntities}
          onClose={() => handleModalWindow({ type: '', note: null })}
        />
      )}
      {isMobile && isNotesPaneOpen && (
        <MobileModal title="Notes" onClose={() => handleNotesPane({ open: false })} onClick={() => {}}>
          <MobileNotes
            entityType={entityType}
            legalEntityNotes={legalEntityNotes}
            aboutInvestorNotes={aboutInvestorNotes}
            handleEntitySelect={handleEntitySelect}
            handleModalWindow={handleModalWindow}
          />
        </MobileModal>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/investors' })} backToTitle={backToTitle} />} />
        <CustomPageTitleWrap withBorder>
          <div>
            Household:{' '}
            {entityId && investorData?.legalEntitiesTotalCount > 1 ? (
              <HouseholdName
                onClick={() => navigate(`/investors/${investorId}?activeTab=${activeTab}&backToTitle=${backToTitle}`, { replace: true })}
              >
                {investorData.investorName}
              </HouseholdName>
            ) : (
              <>{investorData.investorName}</>
            )}
          </div>
          {investorData.advisorName && <ClientAdvisor>Advisor: {investorData.advisorName}</ClientAdvisor>}
        </CustomPageTitleWrap>
        {!isMobile && (
          <NotesPane
            entityType={entityType}
            legalEntityNotes={legalEntityNotes}
            aboutInvestorNotes={aboutInvestorNotes}
            isOpen={isNotesPaneOpen}
            handleEntitySelect={handleEntitySelect}
            handleCloseNotesPane={() => handleNotesPane({ open: false })}
            handleModalWindow={handleModalWindow}
            isModalWindowOpen={modalWindow.isOpen}
          />
        )}
        <EntitySelectWrapper>
          {entitiesSelectOptions?.length === 1 ? (
            <EllipsisWithTooltip title={entitiesSelectOptions[0].label} maxContentWidth={300}>
              <EntityLabel>{entitiesSelectOptions[0].label}</EntityLabel>
            </EllipsisWithTooltip>
          ) : (
            <>
              {entityType === InvestorDetailsEntityTypeEnum.GroupedEntity && <EntityLabel isGroupedEntity>{selectedGroupName}</EntityLabel>}
              <EntitySelect
                value={entityType === InvestorDetailsEntityTypeEnum.Group ? `group-${entityId}` : entityId}
                setValue={handleEntitySelect}
                options={[{ label: 'All Entities', value: '' }, ...entitiesSelectOptions]}
                width="auto"
              />
            </>
          )}
          <EditIconWrap onClick={openEditInvestorPage}>
            <EditIcon fill={themeVar('font.action')} />
          </EditIconWrap>
          {(entityType === InvestorDetailsEntityTypeEnum.UngroupedEntity || entityType === InvestorDetailsEntityTypeEnum.Group) && onHold && (
            <OnHoldTagWrapper>
              <Tag size="lg" styleType="warning">
                On Hold
              </Tag>
            </OnHoldTagWrapper>
          )}
        </EntitySelectWrapper>
        {isDesktop || isTablet || isLargeDesktop ? (
          <>
            <TabsWrapper>
              <CustomTabs
                tabs={tabs}
                activeTab={activeTab}
                setActiveTab={(newActiveTab) => onChangeActiveTab(newActiveTab, activeTab, { backToTitle, isGroup })}
              />
              <NotesButton notesCount={notesCount} openNotesPane={() => handleNotesPane({ open: true })} />
            </TabsWrapper>
            <TabsLine />
          </>
        ) : (
          <TabsWrapper>
            <TabsControl isMobile={isMobile}>
              {isTabMenuOpen ? (
                <CloseMenuIcon fill={themeVar('action.primary')} onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} />
              ) : (
                <BurgerMenuIcon fill={themeVar('action.primary')} onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} />
              )}
              <TabMobileWrap isActive>
                <TabTitle isActive isMobile={isMobile}>
                  {activeTab}
                </TabTitle>
              </TabMobileWrap>
            </TabsControl>
            <NotesButton notesCount={notesCount} openNotesPane={() => handleNotesPane({ open: true })} />
          </TabsWrapper>
        )}
      </MainWrap>
      <MainWrap>
        {isTabMenuOpen && isMobile ? (
          <MainTabsMobile>
            {tabs.map((tab) => (
              <TabsWrapMobile key={tab.value} onClick={() => handleMobileTabClick(tab.value)} isActive={activeTab === tab.value}>
                <TabsDetailsTitleMobile isActive={activeTab === tab.value}>{tab.value}</TabsDetailsTitleMobile>
              </TabsWrapMobile>
            ))}
          </MainTabsMobile>
        ) : (
          children
        )}
      </MainWrap>
    </>
  );
};

const CustomPageTitleWrap = styled(PageTitleWrap)`
  justify-content: space-between;
  font-size: 18px;
  font-weight: 600;
  line-height: 18px;
`;

const ClientAdvisor = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  color: ${themeVar('font.weak')};
`;

const HouseholdName = styled.span`
  cursor: pointer;
  color: ${themeVar('font.action')};
`;

const EntitySelectWrapper = styled.div`
  padding: 10px 50px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${themeVar('layer.1')};
`;

const EntityLabel = styled.div<{ isGroupedEntity?: boolean }>`
  font-size: 33px;
  font-weight: 700;
  line-height: 40px;
  padding: 10px 0;
  white-space: ${({ isGroupedEntity }) => (isGroupedEntity ? 'nowrap' : 'normal')};
  color: ${({ isGroupedEntity }) => (isGroupedEntity ? themeVar('font.action') : themeVar('font.strong'))};
`;

const EntitySelect = styled(Select)`
  color: ${themeVar('font.action')};
  font-size: 33px;
  font-weight: 700;
  line-height: 40px;
  background-color: transparent;
  border: none;
  gap: 10px;
  padding-left: 0;
  padding-right: 0;

  #dropdownSelect {
    font-size: 19px;
    line-height: 26.6px;
    font-weight: 400;
    min-width: 300px;
    left: -20px;
  }
`;

const EditIconWrap = styled.div`
  display: flex;
  cursor: pointer;
  margin-left: 5px;
`;

const OnHoldTagWrapper = styled.div`
  margin-left: auto;
  white-space: nowrap;
`;

const TabsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 60px;
  background: ${themeVar('layer.1')};
`;

const CustomTabs = styled(Tabs)`
  padding-top: 0;
`;
